import { useContext, useEffect, useState } from 'react';

import {
  getLoginConfigurations,
  LoginProjectConfiguration,
  patchFineConfigurations
} from '../../api';
import { Error as ErrorType } from '../../api/setup/getApiError';
import { updateProjectType } from '../../utils';
import { SettingsContext } from './context';
import { useSocialConnections } from './currentSocialConnectionsHook';
import { CurrentProjectProps, CurrentProjectType } from './types';
import { keysToRemove, RemovableKeys } from './utils';

// Module-level cache to track in-flight requests (persists across hook instances)
const inFlightRequests = new Map<string, Promise<LoginProjectConfiguration>>();

export const useCurrentProject = (loginProjectId?: string): CurrentProjectProps => {
  const { currentProject, setCurrentProject } = useContext(SettingsContext);

  const isNewProject = !!(
    loginProjectId &&
    (!currentProject || loginProjectId !== currentProject.id)
  );

  const { currentSocialsFetching, currentSocials } = useSocialConnections(
    loginProjectId,
    isNewProject
  );
  const [fetching, setFetching] = useState<boolean>(true);
  const [fetchingUpdate, setFetchingUpdate] = useState<boolean>(false);
  const [currentProjectType, setCurrentProjectType] = useState<CurrentProjectType>(null);
  const [error, setError] = useState<ErrorType | null>(null);

  useEffect(() => {
    if (!isNewProject || !loginProjectId) {
      setFetching(false);
      return;
    }

    // Checking if there's already an in-flight request for this project
    const existingRequest = inFlightRequests.get(loginProjectId);
    if (existingRequest) {
      setFetching(true);
      existingRequest
        .then((res) => {
          setCurrentProject(res);
          setCurrentProjectType(updateProjectType(res, currentSocials));
        })
        .catch((err) => setError(err))
        .finally(() => setFetching(false));
      return;
    }

    // Creating new request and caching it
    setFetching(true);
    const request = getLoginConfigurations(loginProjectId);
    inFlightRequests.set(loginProjectId, request);

    request
      .then((res) => {
        setCurrentProject(res);
        setCurrentProjectType(updateProjectType(res, currentSocials));
      })
      .catch((err) => setError(err))
      .finally(() => {
        setFetching(false);
        inFlightRequests.delete(loginProjectId);
      });
  }, [loginProjectId, isNewProject]);

  useEffect(
    () => setCurrentProjectType(updateProjectType(currentProject, currentSocials)),
    [currentProject, currentSocials]
  );

  const removeKeys = <T extends Record<string, unknown>>(obj: T): Omit<T, RemovableKeys> => {
    const newObj = { ...obj };

    keysToRemove.forEach((key) => {
      if (key in newObj) {
        delete newObj[key];
      }
    });

    return newObj;
  };

  const updateCurrentProject = async (projectData: LoginProjectConfiguration) => {
    try {
      const projectDataUpdated = removeKeys(projectData);
      setFetchingUpdate(true);
      await patchFineConfigurations(projectDataUpdated, projectData.id);
      setCurrentProject(projectData);
      setFetchingUpdate(false);
      return projectData;
    } finally {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setFetching(false);
      setFetchingUpdate(false);
    }
  };

  const patchCurrentFineProject = async (
    projectData: Partial<LoginProjectConfiguration>,
    projectId: string
  ) => {
    try {
      setFetchingUpdate(true);
      await patchFineConfigurations(projectData, projectId);
      setFetchingUpdate(false);
    } finally {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setFetching(false);
      setFetchingUpdate(false);
    }
  };

  return {
    currentProject,
    error,
    updateCurrentProject,
    patchCurrentFineProject,
    currentProjectFetching: fetching,
    currentProjectType,
    currentSocials,
    currentSocialsFetching,
    currentUpdateFetching: fetchingUpdate
  };
};
